import { useState } from 'react'
import { useTenant } from '@/contexts'
import { useLocationContext } from '@/components/layout/LocationLayout'
import {
  useLocationUsers,
  useAvailableTeamMembers,
  useCreateLocationUser,
  useUpdateLocationUser,
  useDeleteLocationUser,
} from '@/hooks/useLocationUsers'
import {
  LocationUsersTable,
  AssignUserDialog,
  EditLocationRoleDialog,
  RemoveLocationUserDialog,
} from '@/components/settings/locations'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import type { LocationUserTableRow, LocationRole } from '@/types/location-users'

export function LocationUsersTab() {
  const { locationId } = useLocationContext()
  const { currentOrganization } = useTenant()
  const [selectedUser, setSelectedUser] = useState<LocationUserTableRow | null>(null)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)

  const { data: locationUsers = [], isLoading } = useLocationUsers(locationId)
  const { data: availableUsers = [] } = useAvailableTeamMembers(locationId, currentOrganization?.id)
  const createLocationUser = useCreateLocationUser()
  const updateLocationUser = useUpdateLocationUser()
  const deleteLocationUser = useDeleteLocationUser()

  const handleAssignUser = async (data: { user_id: string; location_role: LocationRole }) => {
    await createLocationUser.mutateAsync({
      location_id: locationId,
      user_id: data.user_id,
      location_role: data.location_role,
    })
  }

  const handleEditRole = (user: LocationUserTableRow) => {
    setSelectedUser(user)
    setEditRoleDialogOpen(true)
  }

  const handleUpdateRole = async (newRole: LocationRole) => {
    if (!selectedUser) return
    await updateLocationUser.mutateAsync({
      id: selectedUser.id,
      data: { location_role: newRole },
    })
    setSelectedUser(null)
  }

  const handleRemoveClick = (user: LocationUserTableRow) => {
    setSelectedUser(user)
    setRemoveDialogOpen(true)
  }

  const handleRemoveConfirm = async () => {
    if (!selectedUser) return
    await deleteLocationUser.mutateAsync(selectedUser.id)
    setSelectedUser(null)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Team Members</h2>
          <p className="text-sm text-muted-foreground">
            {locationUsers.length} {locationUsers.length === 1 ? 'member' : 'members'} assigned to this location
          </p>
        </div>
        <Button
          onClick={() => setAssignDialogOpen(true)}
          size="sm"
          className="gap-2"
          disabled={availableUsers.length === 0}
        >
          <Plus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      <LocationUsersTable
        users={locationUsers}
        canManage={true}
        onEditRole={handleEditRole}
        onRemove={handleRemoveClick}
        isLoading={isLoading}
      />

      <AssignUserDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        availableUsers={availableUsers}
        onAssign={handleAssignUser}
        isLoading={createLocationUser.isPending}
      />

      <EditLocationRoleDialog
        open={editRoleDialogOpen}
        onOpenChange={setEditRoleDialogOpen}
        user={selectedUser}
        onUpdateRole={handleUpdateRole}
        isLoading={updateLocationUser.isPending}
      />

      <RemoveLocationUserDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        user={selectedUser}
        onConfirm={handleRemoveConfirm}
        isLoading={deleteLocationUser.isPending}
      />
    </div>
  )
}
